import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import crewHqBracketImage from "../data/crewHqBracketImage";
import CrewHeadquartersTournament from "./CrewHeadquartersTournament";

const TOURNAMENTS = [
  {
    id: "get-active-3v3-75-ovr-2026",
    slug: "get-active-3v3-75-ovr",
    kicker: "LIVE EVENT • 3v3 Park",
    title: "Get Active Tournament",
    image: "/get-active-3v3-bracket.jpg",
    alt: "GET ACTIVE 3v3 Park Tournament Bracket - Max 75 OVR • $300 Prize",
    format: "3v3 Park • Max 75 OVR",
    prize: "$300 Prize",
    description:
      "Current featured event: 3v3 Park (Max 75 OVR). Free entry, 16 teams, 8 East / 8 West, best-of-3 series, live bracket updates, and account-holder registration details live here.",
    detailsPath: "/tournaments",
  },
  {
    id: "gas-up-3v3-crew-hq-2026",
    slug: "gas-up-3v3-crew-headquarters",
    kicker: "UPCOMING EVENT • CREW HEADQUARTERS",
    title: "G.A.S UP Tournament",
    image: crewHqBracketImage,
    alt: "G.A.S UP NBA 2K27 3v3 Crew Headquarters Tournament bracket",
    format: "NBA 2K27 • 3v3 Crew Headquarters • No OVR Cap",
    prize: "$300 Prize • $100 Per Winning Player",
    description:
      "Sixteen teams. Best-of-3 every round with no OVR cap. Captains coordinate which Crew Headquarters hosts the series; teams without a crew can use the G.A.S UP HQ. The championship is played in the G.A.S UP Crew Headquarters. Date is announced after all 16 bracket spots are filled.",
    detailsPath: "/man-cave?event=gas-up-3v3-crew-headquarters",
  },
];

function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function getUserEmail() {
  const raw = localStorage.getItem("ga_user") || localStorage.getItem("user") || "";
  const user = raw ? safeJsonParse(raw) : null;
  return String(user?.email || localStorage.getItem("member_email") || localStorage.getItem("email") || "")
    .trim()
    .toLowerCase();
}

export default function ManCave() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => getUserEmail());
  const [checking, setChecking] = useState(false);
  const [member, setMember] = useState(null);
  const [tournamentIndex, setTournamentIndex] = useState(0);
  const [activeTournaments, setActiveTournaments] = useState(TOURNAMENTS);

  useEffect(() => {
    const refreshEmail = () => setEmail(getUserEmail());
    refreshEmail();
    window.addEventListener("auth:changed", refreshEmail);
    window.addEventListener("storage", refreshEmail);
    return () => {
      window.removeEventListener("auth:changed", refreshEmail);
      window.removeEventListener("storage", refreshEmail);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!email) {
        setMember(false);
        return;
      }
      try {
        setChecking(true);
        const res = await fetch(apiUrl(`/subscriptions/me?email=${encodeURIComponent(email)}`));
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const active = !!(data?.member?.active || data?.member === true);
        setMember(active);
        try {
          localStorage.setItem("member_email", email);
          localStorage.setItem("member_status", active ? "active" : "inactive");
        } catch {}
      } catch {
        if (!cancelled) setMember(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [email]);

  useEffect(() => {
    let cancelled = false;

    async function loadActiveTournaments() {
      try {
        const res = await fetch(apiUrl("/tournaments"));
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !Array.isArray(data?.tournaments)) {
          throw new Error("Could not load tournament status.");
        }

        const summaries = data.tournaments;
        const visible = TOURNAMENTS.map((card) => {
          const summary = summaries.find(
            (candidate) =>
              String(candidate?.id || "") === String(card.id || "") ||
              String(candidate?.slug || "") === String(card.slug || "")
          );
          if (!summary) return card;
          const status = String(summary.status || "").toLowerCase();
          if (["completed", "finished", "archived"].includes(status) || summary.archivedFromHub) {
            return null;
          }
          return { ...card, ...summary, detailsPath: card.detailsPath };
        }).filter(Boolean);

        if (!cancelled) {
          setActiveTournaments(visible);
          setTournamentIndex((current) => Math.min(current, Math.max(0, visible.length - 1)));
        }
      } catch {
        if (!cancelled) setActiveTournaments(TOURNAMENTS);
      }
    }

    loadActiveTournaments();
    return () => {
      cancelled = true;
    };
  }, []);

  const accessText = useMemo(() => {
    if (checking || member === null) return "Checking account access...";
    if (member) return "Active Member";
    return "Tournament registration is available to account holders when an event opens.";
  }, [checking, member]);

  if (searchParams.get("event") === "gas-up-3v3-crew-headquarters") {
    return (
      <>
        <CrewHeadquartersTournament />
        <Link
          to="/tournaments/gas-up-3v3-crew-headquarters/live"
          style={styles.tournamentLiveDock}
          aria-label="Open Tournament Live Hub"
        >
          📺 OPEN TOURNAMENT LIVE HUB
        </Link>
      </>
    );
  }

  const activeTournament = activeTournaments[tournamentIndex] || activeTournaments[0] || null;
  const hasMultipleTournaments = activeTournaments.length > 1;

  const showPreviousTournament = () => {
    setTournamentIndex((current) =>
      current <= 0 ? activeTournaments.length - 1 : current - 1
    );
  };

  const showNextTournament = () => {
    setTournamentIndex((current) =>
      current >= activeTournaments.length - 1 ? 0 : current + 1
    );
  };

  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.kicker}>Competition</div>
        <h1 style={styles.title} className="metallic-title">The Man Cave</h1>
        <p style={styles.subtitle}>
          Tournament hub for event details, registration, live brackets, and competitive nights on GAStreaming.
        </p>
        <div style={member ? styles.accessGood : styles.accessWarn}>{accessText}</div>
      </section>

      <section style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.tournamentHeaderRow}>
            <div>
              <div style={styles.cardKicker}>{activeTournament?.kicker || "TOURNAMENTS"}</div>
              <h2 style={styles.cardTitle}>Tournament Hub</h2>
            </div>

            {hasMultipleTournaments ? (
              <div style={styles.tournamentArrows} aria-label="Browse tournaments">
                <button
                  type="button"
                  onClick={showPreviousTournament}
                  style={styles.arrowBtn}
                  aria-label="Previous tournament"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={showNextTournament}
                  style={styles.arrowBtn}
                  aria-label="Next tournament"
                >
                  ›
                </button>
              </div>
            ) : null}
          </div>

          {activeTournament ? (
            <>
              <div style={styles.tournamentCard}>
                <img
                  src={activeTournament.image}
                  alt={activeTournament.alt}
                  style={styles.tournamentImage}
                />

                <div style={styles.tournamentMetaRow}>
                  <div>
                    <div style={styles.tournamentFormat}>{activeTournament.format}</div>
                    <div style={styles.tournamentPrize}>{activeTournament.prize}</div>
                  </div>

                  <Link
                    to={activeTournament.detailsPath}
                    style={{
                      ...styles.btn,
                      ...styles.tournamentCta,
                    }}
                  >
                    {["in_progress", "active", "live"].includes(String(activeTournament.status || "").toLowerCase())
                      ? "VIEW LIVE BRACKET →"
                      : activeTournament.registrationOpen
                      ? "SIGN UP / VIEW DETAILS →"
                      : "VIEW DETAILS →"}
                  </Link>
                </div>
              </div>

              <p style={styles.cardText}>{activeTournament.description}</p>

              {hasMultipleTournaments ? (
                <div style={styles.tournamentDots} aria-label="Tournament position">
                  {activeTournaments.map((tournament, index) => (
                    <button
                      key={tournament.id}
                      type="button"
                      aria-label={`View tournament ${index + 1}`}
                      onClick={() => setTournamentIndex(index)}
                      style={{
                        ...styles.tournamentDot,
                        ...(index === tournamentIndex ? styles.tournamentDotActive : {}),
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <div style={styles.noActiveTournament}>
              <div style={styles.noActiveTournamentTitle}>No active tournaments right now.</div>
              <div style={styles.noActiveTournamentCopy}>
                Completed tournaments automatically leave this active Tournament Hub so nobody can re-enter a finished event.
              </div>
            </div>
          )}
        </div>

      </section>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1120, margin: "0 auto", padding: "28px 14px 80px" },
  hero: {
    borderRadius: 24,
    padding: "26px 22px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "radial-gradient(900px 420px at 20% 0%, rgba(255,0,64,0.18), transparent 60%), rgba(10,10,14,0.76)",
    boxShadow: "0 22px 70px rgba(0,0,0,0.45)",
  },
  kicker: { color: "#ffb3b3", textTransform: "uppercase", letterSpacing: 3, fontSize: 12, fontWeight: 900 },
  title: { margin: "8px 0 0", fontSize: "clamp(38px, 8vw, 76px)", lineHeight: 0.95, fontFamily: 'Georgia, "Times New Roman", serif' },
  subtitle: { margin: "16px 0 0", maxWidth: 760, color: "rgba(255,255,255,0.78)", lineHeight: 1.55 },
  accessGood: { marginTop: 18, display: "inline-flex", borderRadius: 999, padding: "10px 14px", background: "rgba(0,190,95,0.16)", border: "1px solid rgba(0,255,135,0.24)", fontWeight: 900 },
  accessWarn: { marginTop: 18, display: "inline-flex", borderRadius: 999, padding: "10px 14px", background: "rgba(255,176,0,0.13)", border: "1px solid rgba(255,210,90,0.25)", fontWeight: 900 },
  grid: { margin: "18px auto 0", display: "grid", gridTemplateColumns: "minmax(0, 760px)", justifyContent: "center", gap: 16 },
  card: { borderRadius: 22, padding: 18, background: "rgba(8,8,12,0.74)", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 18px 50px rgba(0,0,0,0.35)" },
  cardKicker: { color: "rgba(255,255,255,0.58)", textTransform: "uppercase", letterSpacing: 2, fontSize: 11, fontWeight: 900 },
  cardTitle: { margin: "8px 0 0", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 28 },
  cardText: { color: "rgba(255,255,255,0.76)", lineHeight: 1.5, minHeight: 72 },
  btn: { display: "inline-flex", alignItems: "center", justifyContent: "center", marginTop: 10, padding: "11px 14px", borderRadius: 999, textDecoration: "none", color: "#fff", fontWeight: 900, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.08)" },
  btnPrimary: { background: "linear-gradient(90deg, rgba(0,186,255,0.88), rgba(255,54,190,0.86))" },
  btnDisabled: { background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.82)" },
  tournamentLiveDock: {
    position: "fixed",
    right: 18,
    bottom: 22,
    zIndex: 5000,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: "calc(100vw - 36px)",
    padding: "13px 18px",
    borderRadius: 999,
    textDecoration: "none",
    color: "#fff",
    fontWeight: 950,
    fontSize: 13,
    letterSpacing: 0.35,
    border: "1px solid rgba(116,219,255,.9)",
    background: "linear-gradient(90deg, rgba(82,43,255,.98), rgba(0,188,255,.96))",
    boxShadow: "0 16px 42px rgba(0,0,0,.58), 0 0 26px rgba(82,43,255,.34)",
  },
  noActiveTournament: {
    border: "1px dashed rgba(255,255,255,0.16)",
    borderRadius: 18,
    padding: "28px 18px",
    background: "rgba(0,0,0,0.22)",
  },
  noActiveTournamentTitle: { color: "#fff", fontWeight: 900, fontSize: 18 },
  noActiveTournamentCopy: { marginTop: 8, color: "rgba(255,255,255,0.68)", lineHeight: 1.55 },
  tournamentHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  tournamentArrows: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  arrowBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.16)",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1,
    cursor: "pointer",
  },
  tournamentCard: {
    margin: "14px 0 18px",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(225,6,19,0.35)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
    background: "#0a0a0e",
  },
  tournamentImage: {
    width: "100%",
    height: "auto",
    display: "block",
    filter: "contrast(1.05) saturate(1.1)",
  },
  tournamentMetaRow: {
    padding: "12px 16px",
    background: "linear-gradient(180deg, rgba(10,10,14,0.95), rgba(8,8,12,0.98))",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  tournamentFormat: { color: "#fff", fontWeight: 900, fontSize: 15, letterSpacing: 0.5 },
  tournamentPrize: { color: "#ff4d4d", fontWeight: 900, fontSize: 18, marginTop: 2 },
  tournamentCta: {
    marginTop: 0,
    background: "linear-gradient(180deg, #e10613, #8c0000)",
    border: "1px solid #ff4d4d",
    color: "#fff",
    fontSize: 12,
    padding: "9px 15px",
  },
  tournamentDots: {
    display: "flex",
    justifyContent: "center",
    gap: 7,
    marginTop: 10,
  },
  tournamentDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.25)",
    background: "rgba(255,255,255,0.12)",
    padding: 0,
    cursor: "pointer",
  },
  tournamentDotActive: {
    width: 22,
    background: "#e10613",
    borderColor: "rgba(255,77,77,0.75)",
  },
};